import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Trash2, Plus, RefreshCw } from "lucide-react";
import { generateSequenceColor, generateDefaultSequenceConfigs, calculateBrightness, shouldUseWhiteText } from "@/lib/sequence-colors";
import { invalidateSequenceConfigsCache } from "@/lib/sequence-config-utils";
import { NUMERIC_SEQUENCES, ALPHA_SEQUENCES, compareSequenceIds, isValidSequenceId } from "@/lib/sequence-utils";
import type { SequenceTemplate } from "@shared/schema";

export function SequenceSettings() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [editingTemplate, setEditingTemplate] = useState<number | null>(null);
  const [editName, setEditName] = useState<string>("");
  const [editColor, setEditColor] = useState<string>("");
  const [editDescription, setEditDescription] = useState<string>("");
  const [newSequenceNumber, setNewSequenceNumber] = useState<string>("");

  // Fetch sequence templates
  const { data: sequenceTemplates = [], isLoading } = useQuery({
    queryKey: ["/api/sequence-templates"],
    queryFn: async (): Promise<SequenceTemplate[]> => {
      const response = await apiRequest("GET", "/api/sequence-templates");
      if (!response.ok) throw new Error("Failed to fetch sequence templates");
      return response.json();
    }
  });

  // Create sequence template
  const createSequenceTemplateMutation = useMutation({
    mutationFn: async (template: { sequenceNumber: string; name: string; description: string; defaultColor: string; isActive: boolean }) => {
      const response = await apiRequest("POST", "/api/sequence-templates", template);
      if (!response.ok) throw new Error("Failed to create sequence template");
      return response.json();
    },
    onSuccess: () => {
      invalidateSequenceConfigsCache();
      queryClient.invalidateQueries({ queryKey: ["/api/sequence-templates"] });
      queryClient.invalidateQueries({ queryKey: ["/api/years"] });
      setNewSequenceNumber("");
      toast({ title: "Sequence template created successfully" });
    },
    onError: () => {
      toast({ title: "Failed to create sequence template", variant: "destructive" });
    }
  });

  // Update sequence template
  const updateSequenceTemplateMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: number; updates: Partial<SequenceTemplate> }) => {
      const response = await apiRequest("PATCH", `/api/sequence-templates/${id}`, updates);
      if (!response.ok) throw new Error("Failed to update sequence template");
      return response.json();
    },
    onSuccess: () => {
      invalidateSequenceConfigsCache();
      queryClient.invalidateQueries({ queryKey: ["/api/sequence-templates"] });
      queryClient.invalidateQueries({ queryKey: ["/api/years"] });
      setEditingTemplate(null);
      setEditName("");
      setEditColor("");
      setEditDescription("");
      toast({ title: "Sequence template updated successfully" });
    },
    onError: () => {
      toast({ title: "Failed to update sequence configuration", variant: "destructive" });
    }
  });

  // Delete sequence template
  const deleteSequenceTemplateMutation = useMutation({
    mutationFn: async (templateId: number) => {
      const response = await apiRequest("DELETE", `/api/sequence-templates/${templateId}`);
      if (!response.ok) throw new Error("Failed to delete sequence template");
      return response.json();
    },
    onSuccess: () => {
      invalidateSequenceConfigsCache();
      queryClient.invalidateQueries({ queryKey: ["/api/sequence-templates"] });
      queryClient.invalidateQueries({ queryKey: ["/api/years"] });
      toast({ title: "Sequence template deleted successfully" });
    },
    onError: () => {
      toast({ title: "Failed to delete sequence template", variant: "destructive" });
    }
  });

  // Initialize default templates
  const initializeDefaultsMutation = useMutation({
    mutationFn: async () => {
      const defaultConfigs = generateDefaultSequenceConfigs();
      const existingIds = sequenceTemplates.map(t => t.sequenceNumber);
      
      // Only create templates for sequences that don't exist yet
      const newTemplates = defaultConfigs.filter(config => !existingIds.includes(config.sequenceNumber));
      
      const promises = newTemplates.map(config =>
        apiRequest("POST", "/api/sequence-templates", {
          sequenceNumber: config.sequenceNumber,
          name: `Sequence ${config.sequenceNumber}`,
          description: `Template for sequence ${config.sequenceNumber}`,
          defaultColor: config.color,
          isActive: config.isActive
        })
      );
      
      const responses = await Promise.all(promises);
      const failedResponses = responses.filter(r => !r.ok);
      
      if (failedResponses.length > 0) {
        throw new Error(`${failedResponses.length} templates failed to create`);
      }
      
      return { created: newTemplates.length };
    },
    onSuccess: (data) => {
      invalidateSequenceConfigsCache();
      queryClient.invalidateQueries({ queryKey: ["/api/sequence-templates"] });
      queryClient.invalidateQueries({ queryKey: ["/api/years"] });
      toast({ 
        title: `Default templates created`, 
        description: `Successfully created ${data.created} sequence templates`
      });
    },
    onError: () => {
      toast({ title: "Failed to initialize default templates", variant: "destructive" });
    }
  });

  const handleEditClick = (template: SequenceTemplate) => {
    setEditingTemplate(template.id);
    setEditName(template.name || "");
    setEditColor(template.defaultColor);
    setEditDescription(template.description || "");
  };

  const handleEditSave = () => {
    if (editingTemplate !== null && editColor) {
      updateSequenceTemplateMutation.mutate({
        id: editingTemplate,
        updates: { 
          name: editName || null,
          defaultColor: editColor,
          description: editDescription || null
        }
      });
    }
  };

  const handleEditCancel = () => {
    setEditingTemplate(null);
    setEditName("");
    setEditColor("");
    setEditDescription("");
  };

  const handleAddNewSequence = () => {
    const sequenceId = newSequenceNumber.toUpperCase().trim();
    
    // Validate format
    if (!isValidSequenceId(sequenceId)) {
      toast({ title: "Please enter a valid sequence identifier (MST, 01-99, or A-Z)", variant: "destructive" });
      return;
    }

    if (sequenceTemplates.some(t => t.sequenceNumber === sequenceId)) {
      toast({ title: "Sequence identifier already exists", variant: "destructive" });
      return;
    }

    createSequenceTemplateMutation.mutate({
      sequenceNumber: sequenceId,
      name: `Sequence ${sequenceId}`,
      description: `Template for sequence ${sequenceId}`,
      defaultColor: generateSequenceColor(sequenceId),
      isActive: true
    });
  };

  const getNextAvailableSequence = (): string => {
    const existingIds = sequenceTemplates.map(t => t.sequenceNumber);
    for (const id of NUMERIC_SEQUENCES) {
      if (!existingIds.includes(id)) {
        return id;
      }
    }
    return "01";
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white">Sequence Color Configuration</h3>
          <p className="text-sm text-gray-400">
            Global color settings for sequence numbers across all projects. Every project's "Seq. 01" will use the same color.
          </p>
        </div>
        
        {sequenceTemplates.length === 0 && (
          <Button
            onClick={() => initializeDefaultsMutation.mutate()}
            disabled={initializeDefaultsMutation.isPending}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {initializeDefaultsMutation.isPending ? (
              <RefreshCw className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Plus className="w-4 h-4 mr-2" />
            )}
            Initialize Defaults (1-99)
          </Button>
        )}
      </div>

      {/* Add New Sequence */}
      <Card className="navy-card border-gray-600">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-base">Add New Sequence</CardTitle>
          <CardDescription className="text-gray-400">
            Create a new sequence color configuration
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-end space-x-3">
            <div className="flex-1">
              <Label htmlFor="newSequenceNumber" className="text-gray-300 text-sm">
                Sequence Number
              </Label>
              <Input
                id="newSequenceNumber"
                type="number"
                min="1"
                max="99"
                value={newSequenceNumber}
                onChange={(e) => setNewSequenceNumber(e.target.value)}
                placeholder={`Next available: ${getNextAvailableSequence()}`}
                className="navy-bg border-gray-600 text-white h-8"
              />
            </div>
            <Button
              onClick={handleAddNewSequence}
              disabled={createSequenceTemplateMutation.isPending || !newSequenceNumber}
              size="sm"
              className="bg-blue-600 hover:bg-blue-700 h-8"
            >
              {createSequenceTemplateMutation.isPending ? (
                <RefreshCw className="w-3 h-3 animate-spin" />
              ) : (
                <Plus className="w-3 h-3" />
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Sequence Configurations List */}
      <Card className="navy-card border-gray-600">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-base">
            Sequence Templates ({sequenceTemplates.length} configured)
          </CardTitle>
          <CardDescription className="text-gray-400">
            Click on a color to edit it. Colors are automatically optimized for 20-40% brightness with white text.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-64">
            <div className="space-y-2">
              {sequenceTemplates
                .sort((a, b) => compareSequenceIds(a.sequenceNumber, b.sequenceNumber))
                .map((template) => (
                  <div key={template.id} className="flex items-center justify-between p-2 rounded border border-gray-600 hover:border-gray-500 transition-colors">
                    <div className="flex items-center space-x-3">
                      <span className="text-gray-300 font-medium w-12">
                        Seq. {template.sequenceNumber}
                      </span>
                      
                      {editingTemplate === template.id ? (
                        <div className="flex items-center space-x-2">
                          <Input
                            type="text"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            placeholder="Template name"
                            className="w-32 h-7 text-xs bg-gray-700 border-gray-600 text-white"
                          />
                          <Input
                            type="color"
                            value={editColor}
                            onChange={(e) => setEditColor(e.target.value)}
                            className="w-12 h-7 border-gray-600 cursor-pointer"
                          />
                          <span className="text-xs text-gray-400">
                            {calculateBrightness(editColor)}% brightness
                          </span>
                          <Button
                            size="sm"
                            onClick={handleEditSave}
                            disabled={updateSequenceTemplateMutation.isPending}
                            className="h-6 px-2 bg-green-600 hover:bg-green-700"
                          >
                            Save
                          </Button>
                          <Button
                            size="sm"
                            onClick={handleEditCancel}
                            variant="outline"
                            className="h-6 px-2 border-gray-600"
                          >
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => handleEditClick(template)}
                            className="relative group"
                          >
                            <Badge
                              className="px-3 py-1 text-xs font-medium border cursor-pointer transition-all group-hover:scale-105"
                              style={{
                                backgroundColor: template.defaultColor,
                                color: shouldUseWhiteText(template.defaultColor) ? '#ffffff' : '#000000',
                                borderColor: shouldUseWhiteText(template.defaultColor) ? '#ffffff' : '#000000'
                              }}
                            >
                              {template.name || `Seq. ${template.sequenceNumber.toString().padStart(2, '0')}`}
                            </Badge>
                            <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                              {template.defaultColor} • {calculateBrightness(template.defaultColor)}% brightness
                            </div>
                          </button>
                          
                          <span className="text-xs text-gray-500">
                            {template.defaultColor} • {calculateBrightness(template.defaultColor)}%
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              
              {sequenceTemplates.length === 0 && (
                <div className="text-center py-8 text-gray-400">
                  <p>No sequence templates found.</p>
                  <p className="text-sm mt-1">Click "Initialize Defaults" to create templates for sequences 1-20.</p>
                </div>
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>

      {sequenceTemplates.length > 0 && (
        <div className="text-xs text-gray-500 space-y-1">
          <p>• Colors are automatically generated with 20-40% brightness for optimal visibility</p>
          <p>• White text is used on all sequence labels for consistency</p>
          <p>• Color changes apply globally to all projects using the same sequence number</p>
        </div>
      )}
    </div>
  );
}